import React from 'react';
import { SlideLayout } from '../ui/SlideLayout';
import { MapPinIcon, NavigationIcon, MousePointerClickIcon, BookOpenIcon, MenuIcon, LayoutIcon, FileTextIcon, CodeIcon, SmartphoneIcon, AlertCircleIcon } from 'lucide-react';
interface BlockerCardProps {
  icon: any;
  title: string;
  description: string;
}
const BlockerCard: React.FC<BlockerCardProps> = ({
  icon: Icon,
  title,
  description
}) => <>
    <div className="glass-card p-4 sm:p-6 border-l-4 border-l-red-500" data-id="element-294">
      <div className="flex items-start gap-3 sm:gap-4" data-id="element-295">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0 mt-0.5" data-id="element-296">
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" data-id="element-297" />
        </div>
        <div className="min-w-0" data-id="element-298">
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1 sm:mb-2" data-id="element-299">
            {title}
          </h3>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed" data-id="element-300">
            {description}
          </p>
        </div>
      </div>
    </div>
  </>;
export function RevenueBlockers() {
  return <>
      <SlideLayout data-id="element-301">
        <div className="mb-6 sm:mb-10" data-id="element-302">
          <div className="w-12 h-1 bg-red-500 mb-4 sm:mb-6 rounded-full" data-id="element-303" />
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-304">
            The Challenge & Revenue Blockers
          </h2>
          <p className="text-sm sm:text-xl text-slate-500" data-id="element-305">
            Structural and strategic gaps preventing your website from
            converting visitors.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-12" data-id="element-306">
          <BlockerCard icon={MapPinIcon} title="No Location-Specific Pages" description="Missing out on local search visibility and targeted local traffic for key service areas." data-id="element-307" />
          <BlockerCard icon={NavigationIcon} title="Unclear Site Structure" description="Website structure and navigation are not optimized for conversion; users struggle to find services." data-id="element-308" />
          <BlockerCard icon={MousePointerClickIcon} title="Weak or Inconsistent CTAs" description="CTAs do not guide users effectively toward taking action, such as applying for a mortgage or getting a quote." data-id="element-309" />
          <BlockerCard icon={BookOpenIcon} title="No Blog Section" description="Lacks a blog section, making it hard to target long-tail keywords and provide users with valuable, SEO-optimized content." data-id="element-310" />
          <BlockerCard icon={MenuIcon} title="Poor Menu Organization" description="Menu items are not user-centric and need to be more logically organized to improve user experience and navigation." data-id="element-311" />
          <BlockerCard icon={LayoutIcon} title="No Commercial Landing Pages" description="Website focuses on guides but lacks commercial-focused landing pages that lead users directly to service offerings." data-id="element-312" />
          <BlockerCard icon={FileTextIcon} title="Limited Content" description="Currently only 'Our Team' and basic service pages — could be expanded with more detailed, service-oriented content." data-id="element-313" />
          <BlockerCard icon={CodeIcon} title="No Schema Markup" description="Missing schema markup to help search engines understand the website's content structure." data-id="element-314" />
          <BlockerCard icon={SmartphoneIcon} title="Poor Mobile Performance" description="Mobile speed at 61/100 affects user experience, engagement, and causes high bounce rates from mobile users." data-id="element-315" />
        </div>

        <div className="bg-gradient-to-r from-slate-50 to-white border border-gold-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-8 relative overflow-hidden shadow-sm" data-id="element-316">
          <div className="absolute top-0 right-0 w-40 sm:w-64 h-40 sm:h-64 bg-gold-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-317" />

          <h3 className="text-base sm:text-xl font-bold text-gold-600 mb-3 sm:mb-6 flex items-center gap-2" data-id="element-318">
            <AlertCircleIcon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-319" />
            Business Impact
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 sm:gap-y-4 gap-x-8" data-id="element-320">
            <div className="flex items-center gap-3" data-id="element-321">
              <div className="w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" data-id="element-322" />
              <span className="text-xs sm:text-base text-slate-700 font-medium" data-id="element-323">
                Not ranking for high-intent keywords like "mortgage broker +
                city"
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-324">
              <div className="w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" data-id="element-325" />
              <span className="text-xs sm:text-base text-slate-700 font-medium" data-id="element-326">
                Losing local traffic to competitors with better local
                optimization
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-327">
              <div className="w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" data-id="element-328" />
              <span className="text-xs sm:text-base text-slate-700 font-medium" data-id="element-329">
                Visitors don't know where to go, leading to lower conversion
                rates
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-330">
              <div className="w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" data-id="element-331" />
              <span className="text-xs sm:text-base text-slate-700 font-medium" data-id="element-332">
                Reduced visibility due to lack of local pages and poor schema
                markup
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-333">
              <div className="w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" data-id="element-334" />
              <span className="text-xs sm:text-base text-slate-700 font-medium" data-id="element-335">
                High bounce rate from mobile users due to slow load times
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-336">
              <div className="w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" data-id="element-337" />
              <span className="text-xs sm:text-base text-slate-700 font-medium" data-id="element-338">
                Difficulty establishing trust without blog content and organized
                pages
              </span>
            </div>
          </div>
        </div>
      </SlideLayout>
    </>;
}